import * as ko from "knockout";
import {IRouteDefinitionParams, ITableColumn, TBaseOrObservableType} from '../../index';
import {global, lm} from "../../appContext";
import {nsRoute} from "../../util/route";
import {nsLanguage} from "../../util/nsLanguage";
import {nsSearchToggle} from "../search/nsSearchToggle";
import {getAnyValue} from "../../util/misc";
import {IParams as INavigationParams} from "../search/navigation"
import {storeObservable} from "../../util/sessionstore";
import {KOSubscriptionTracker} from "../../util/KOSubscriptionTracker";
import {IDropdownButton} from "../button";
import {IParams as IEllipsisDropdownParams} from "../button/ellipsis-dropdown";
import ParsedRouteDataDecorator = nsRoute.ParsedRouteDataDecorator;
import ItemCheckbox = nsSearchToggle.ItemCheckbox;
import selected_listHtml from './selected-list.html?raw';

export let template = selected_listHtml;

/** Apply shared action-button chrome used by page-shell / selected-list command bars. */
function withActionChrome(button: IDropdownButton): IDropdownButton {
    return {
        ...button,
        cls: [button.cls, 'btn-action'].filter(Boolean).join(' '),
    };
}

/** Hybrid command bar for the selected-list summary: quick stay visible; the rest sit behind "Mer". */
export interface ISelectedListActions {
    quickActions?: IDropdownButton[]
    moreActions?: IDropdownButton[]
}

export interface ISelectedListParams<T> extends IRouteDefinitionParams, ISelectedListActions {
    checkedKey?: keyof T
    getValue?: (key: any, data: T) => any
    selectedObjects: KnockoutObservableArray<T>
    selectedObjectsSorted?: TBaseOrObservableType<T[]> | (() => T[])
    showHelp: boolean
    table: TBaseOrObservableType<ITableColumn[]>
    getHeaderLabel?: (col: ITableColumn<T>) => string,
    getHeaderTitle?: (col: ITableColumn<T>) => string,
}

export class viewModel<T> extends ParsedRouteDataDecorator {
    protected readonly selectedObjects: KnockoutObservableArray<T>;
    protected readonly selectedObjectsSorted: TBaseOrObservableType<T[]> | (() => T[]) | undefined
    private readonly showHelp: boolean;
    private readonly table: TBaseOrObservableType<ITableColumn[]>;
    private readonly toggle!: (o: T) => void;
    private readonly isChecked!: (o: T) => boolean;
    private readonly checkedKey: keyof T | undefined
    private readonly pagenr: KnockoutObservable<number> = ko.observable(0)
    private readonly kosub: KOSubscriptionTracker = new KOSubscriptionTracker()
    private readonly searchresultsprpage = storeObservable("num.search.results");
    private readonly quickActions: IDropdownButton[];
    private readonly moreActions: IDropdownButton[];
    private readonly moreDropdown: IEllipsisDropdownParams;

    constructor(protected params: ISelectedListParams<T>) {
        super(params);

        const clearSelection: IDropdownButton = {
            label: 'Clear selection',
            onClick: () => {
                this.onRemoveAllSelectedClick()
            },
        }

        const callerQuick = params.quickActions ?? []
        const callerMore = params.moreActions ?? []
        // Clear selection always leads; caller quick/more follow (same role as the old split main + menu).
        this.quickActions = (callerQuick.length || callerMore.length
            ? [clearSelection, ...callerQuick]
            : [clearSelection]
        ).map(withActionChrome)
        this.moreActions = callerMore
        this.moreDropdown = {
            buttons: this.moreActions,
            icon: 'fal fa-chevron-down',
            label: 'pageshell.actions.more',
            title: 'pageshell.actions.more',
            btnClass: 'btn btn-action btn-heading-actions-more',
        }

        this.checkedKey = params.checkedKey
        this.selectedObjects = params.selectedObjects;
        this.selectedObjectsSorted = params.selectedObjectsSorted;
        this.table = params.table;
        this.showHelp = params.showHelp ?? true;
        this.getValue = params.getValue ?? this.getValue

        const _selectedObjects = this.selectedObjectsSorted ?? this.selectedObjects
        if (ko.isObservable(_selectedObjects)) {
            this.kosub.subscribe(_selectedObjects, () => {
                const total = _selectedObjects().length
                const resultsPerPage = this.searchresultsprpage()
                const maxPagenr = Math.ceil(total / resultsPerPage) - 1
                if (this.pagenr() > maxPagenr) {
                    this.pagenr(maxPagenr)
                }
            })
        }

        this.kosub.subscribe(this.searchresultsprpage, () => {
            this.pagenr(0)
        })
    }

    protected getNumObjectsLabel(): string {
        return nsLanguage.getNumObjectsLabel(this.selectedObjects().length, this.__objectTypeId!, !!this.__routeDef.manual)
    }

    onRemoveAllSelectedClick() {
        const selectedObjects = this.selectedObjects();
        global.snackbarData({
            message: `subject is removed|s:${this.getNumObjectsLabel()}`,
            undo: () => {
                this.selectedObjects(selectedObjects);
            }
        });
        this.selectedObjects([]);
    }

    protected selectedLabel(): string {
        const numObjectsLabel = this.getNumObjectsLabel()
        return `((selection)) [${numObjectsLabel}]`;
    }

    private getValue(key: keyof T, data: T): any {
        return data[key]
    }

    private getItemCheckbox(item: T): ItemCheckbox<T> {
        return new ItemCheckbox({
            item: item,
            key: this.checkedKey ?? 'id' as keyof T,
            selected: this.selectedObjects,
        })
    }

    private getList(): T[] {
        const data = getAnyValue(this.selectedObjectsSorted ?? this.selectedObjects)
        const resultsPerPage = storeObservable('num.search.results')()
        const start = this.pagenr() * resultsPerPage
        const end = start + resultsPerPage
        return data.slice(start, end)
    }

    protected getHeaderLabel(col: ITableColumn<T>): string {
        return this.params.getHeaderLabel?.(col) ?? (lm.t(String(getAnyValue(col.label) ?? col.key)) ?? '')
    }

    protected getHeaderTitle(col: ITableColumn<T>): string {
        return this.params.getHeaderTitle?.(col) ?? col.getHeaderTitle?.() ?? ''
    }

    private getNavigationParams(): INavigationParams {
        return {
            pagenr: this.pagenr,
            data: this.selectedObjects,
        }
    }

    private hasMoreActions(): boolean {
        return this.moreActions.some((btn) => btn.validator?.() ?? true)
    }

    override dispose() {
        super.dispose();
        this.kosub.unsuscribeAll()
    }

}
